import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  Command,
  Plus,
  MapPin,
  Calendar,
  User,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  Clock,
  PaperPlaneTilt,
  Link as LinkIcon,
  ArrowSquareOut,
  Users,
  Queue,
} from '@phosphor-icons/react';

export const CommandCenterPage = () => {
  const { api, user, isSupervisorPlus, isAuditor, isCrewLeaderPlus } = useAuth();
  const { openCopilot, setEntityContext } = useCopilot();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createWOOpen, setCreateWOOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [sendToCanvassLoading, setSendToCanvassLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stations, setStations] = useState([]);

  // Crew Request State
  const [crewRequirements, setCrewRequirements] = useState({
    crew_leader: 1,
    mechanic: 0,
    crew_member: 0,
  });
  const [crewRequestMode, setCrewRequestMode] = useState('DRAFT');
  const [crewRequestPriority, setCrewRequestPriority] = useState('normal');
  const [crewRequestNotes, setCrewRequestNotes] = useState('');
  const [crewRequestLoading, setCrewRequestLoading] = useState(false);
  const [crewRequestDialogOpen, setCrewRequestDialogOpen] = useState(false);

  // New work order form
  const [newWO, setNewWO] = useState({
    title: '',
    description: '',
    crew_leader_id: '',
    location: '',
    start_date: '',
    due_date: '',
    required_classification: '',
    station_id: '',
    needs_canvass: true,  // Default: auto-sync to Quick Canvass
  });

  // Update form
  const [newUpdate, setNewUpdate] = useState({
    progress_percent: 0,
    notes: '',
    risks: '',
    blockers: '',
    next_actions: '',
  });

  useEffect(() => {
    fetchWorkOrders();
    fetchStations();
    if (isSupervisorPlus()) {
      fetchUsers();
    }
  }, []);

  const fetchStations = async () => {
    try {
      const response = await api.get('/stations');
      setStations(response.data || []);
    } catch (error) {
      console.error('Failed to fetch stations');
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const response = await api.get('/workorders');
      setWorkOrders(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchUpdates = async (woId) => {
    try {
      const response = await api.get(`/workorders/${woId}/updates`);
      setUpdates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch updates:', error);
      setUpdates([]);
    }
  };

  const handleSelectWO = async (wo) => {
    // Fetch full work order details including linked job
    try {
      const response = await api.get(`/workorders/${wo.id}`);
      setSelectedWO(response.data);
      setEntityContext({ work_order_id: wo.id });
      await fetchUpdates(wo.id);
    } catch (error) {
      // Fallback to basic data
      setSelectedWO(wo);
      setEntityContext({ work_order_id: wo.id });
      await fetchUpdates(wo.id);
    }
  };

  const handleSendToCanvass = async () => {
    if (!selectedWO) return;
    
    setSendToCanvassLoading(true);
    try {
      const response = await api.post(`/canvass/jobs/from-workorder`, {
        work_order_id: selectedWO.id
      });
      toast.success(`Work order ${response.data.action} in Quick Canvass`);
      
      if (response.data.locked_fields) {
        toast.info(`Some fields locked due to existing bids`);
      }
      
      // Refresh the selected WO to get the linked job info
      const updatedWO = await api.get(`/workorders/${selectedWO.id}`);
      setSelectedWO(updatedWO.data);
      await fetchWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send to Quick Canvass');
    } finally {
      setSendToCanvassLoading(false);
    }
  };

  const navigateToCanvassJob = (jobId) => {
    navigate(`/canvass?job=${jobId}`);
  };

  // Crew Request Functions
  const handleSaveCrewRequirements = async () => {
    if (!selectedWO) return;
    
    try {
      const total = Object.values(crewRequirements).reduce((sum, val) => sum + val, 0);
      await api.patch(`/workorders/${selectedWO.id}/crew-requirements`, {
        crew_requirements: crewRequirements,
        crew_size_total: total
      });
      toast.success('Crew requirements saved');
      
      // Refresh the work order
      const updatedWO = await api.get(`/workorders/${selectedWO.id}`);
      setSelectedWO(updatedWO.data);
      await fetchWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save crew requirements');
    }
  };

  const handleQueueCrewRequest = async () => {
    if (!selectedWO) return;
    
    setCrewRequestLoading(true);
    try {
      // First save the crew requirements if modified
      const total = Object.values(crewRequirements).reduce((sum, val) => sum + val, 0);
      if (total > 0) {
        await api.patch(`/workorders/${selectedWO.id}/crew-requirements`, {
          crew_requirements: crewRequirements,
          crew_size_total: total
        });
      }
      
      // Then queue the request
      await api.post(`/workorders/${selectedWO.id}/queue-crew-request`, {
        mode: crewRequestMode,
        priority: crewRequestPriority,
        notes: crewRequestNotes
      });
      
      toast.success('Crew request queued for Quick Canvass!');
      setCrewRequestDialogOpen(false);
      
      // Reset form
      setCrewRequestNotes('');
      setCrewRequestPriority('normal');
      
      // Refresh
      const updatedWO = await api.get(`/workorders/${selectedWO.id}`);
      setSelectedWO(updatedWO.data);
      await fetchWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to queue crew request');
    } finally {
      setCrewRequestLoading(false);
    }
  };

  // Initialize crew requirements when selecting a work order
  useEffect(() => {
    if (selectedWO?.crew_requirements) {
      setCrewRequirements({
        crew_leader: selectedWO.crew_requirements.crew_leader || 1,
        mechanic: selectedWO.crew_requirements.mechanic || 0,
        crew_member: selectedWO.crew_requirements.crew_member || 0,
      });
    } else {
      setCrewRequirements({ crew_leader: 1, mechanic: 0, crew_member: 0 });
    }
  }, [selectedWO]);

  // Ask Agent prompts for work order context
  const woAgentPrompts = selectedWO ? [
    { label: 'Summarize Updates', prompt: 'Summarize all updates and progress for this work order.', context: { work_order_id: selectedWO.id } },
    { label: 'Identify Blockers', prompt: 'Identify all blockers and risks reported for this work order.', context: { work_order_id: selectedWO.id } },
    { label: 'Draft Daily Report', prompt: 'Draft a daily status report for this work order.', context: { work_order_id: selectedWO.id } },
  ] : [];

  const handleCreateWO = async (e) => {
    e.preventDefault();
    try {
      await api.post('/workorders', newWO);
      toast.success('Work order created successfully');
      setCreateWOOpen(false);
      setNewWO({
        title: '',
        description: '',
        crew_leader_id: '',
        location: '',
        start_date: '',
        due_date: '',
        required_classification: '',
        station_id: '',
        needs_canvass: true,
      });
      
      // Show info about canvass sync
      if (response.data.canvass_job_id) {
        toast.success(`Work order created and synced to Quick Canvass`);
      }
      await fetchWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create work order');
    }
  };

  const handleSubmitUpdate = async () => {
    if (!selectedWO) return;

    try {
      await api.post(`/workorders/${selectedWO.id}/updates`, newUpdate);
      toast.success('Update submitted successfully');
      setUpdateDialogOpen(false);
      setNewUpdate({
        progress_percent: 0,
        notes: '',
        risks: '',
        blockers: '',
        next_actions: '',
      });
      await fetchUpdates(selectedWO.id);
      await fetchWorkOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit update');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-100 text-emerald-800">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-100 text-blue-800">In Progress</Badge>;
      case 'not_started':
        return <Badge className="bg-slate-100 text-slate-800">Not Started</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const canSubmitUpdate = () => {
    if (!selectedWO) return false;
    if (isAuditor()) return false;
    if (isSupervisorPlus()) return true;
    if (isCrewLeaderPlus() && selectedWO.crew_leader_id === user?.id) return true;
    return false;
  };

  const crewLeaders = users.filter(u => 
    u.roles?.includes('CREW_LEADER') || u.roles?.includes('SUPERVISOR') || u.roles?.includes('ADMIN')
  );

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="command-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
            Command Center
          </h1>
          <p className="text-slate-600">
            Manage work orders and track project progress
          </p>
        </div>
        {isSupervisorPlus() && !isAuditor() && (
          <Dialog open={createWOOpen} onOpenChange={setCreateWOOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-wo-btn">
                <Plus size={20} className="mr-2" />
                Create Work Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Work Order</DialogTitle>
                <DialogDescription>
                  Create a new work order and assign it to a crew leader
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateWO} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="wo-title">Title</Label>
                  <Input
                    id="wo-title"
                    value={newWO.title}
                    onChange={(e) => setNewWO({ ...newWO, title: e.target.value })}
                    required
                    data-testid="wo-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wo-description">Description</Label>
                  <Textarea
                    id="wo-description"
                    value={newWO.description}
                    onChange={(e) => setNewWO({ ...newWO, description: e.target.value })}
                    data-testid="wo-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Assign to Crew Leader</Label>
                  <Select
                    value={newWO.crew_leader_id}
                    onValueChange={(value) => setNewWO({ ...newWO, crew_leader_id: value })}
                  >
                    <SelectTrigger data-testid="wo-crew-leader-select">
                      <SelectValue placeholder="Select crew leader" />
                    </SelectTrigger>
                    <SelectContent>
                      {crewLeaders.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name} ({u.roles?.join(', ')})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wo-location">Location</Label>
                    <Input
                      id="wo-location"
                      value={newWO.location}
                      onChange={(e) => setNewWO({ ...newWO, location: e.target.value })}
                      data-testid="wo-location-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="wo-start">Start Date</Label>
                    <Input
                      id="wo-start"
                      type="date"
                      value={newWO.start_date}
                      onChange={(e) => setNewWO({ ...newWO, start_date: e.target.value })}
                      data-testid="wo-start-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="wo-due">Due Date</Label>
                  <Input
                    id="wo-due"
                    type="date"
                    value={newWO.due_date}
                    onChange={(e) => setNewWO({ ...newWO, due_date: e.target.value })}
                    data-testid="wo-due-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wo-classification">Required Classification</Label>
                    <Input
                      id="wo-classification"
                      value={newWO.required_classification}
                      onChange={(e) => setNewWO({ ...newWO, required_classification: e.target.value })}
                      placeholder="e.g., Electrician, Lineman"
                      data-testid="wo-classification-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Station</Label>
                    <Select
                      value={newWO.station_id || "none"}
                      onValueChange={(value) => setNewWO({ ...newWO, station_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="wo-station-select">
                        <SelectValue placeholder="Select station (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {stations.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name} {s.code && `(${s.code})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div>
                    <Label className="font-medium">Send to Quick Canvass</Label>
                    <p className="text-xs text-slate-500">Automatically create a job for bidding</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={newWO.needs_canvass}
                      onChange={(e) => setNewWO({ ...newWO, needs_canvass: e.target.checked })}
                      data-testid="wo-needs-canvass-toggle"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-primary" data-testid="submit-wo-btn">
                    Create Work Order
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-display font-bold text-slate-900">
                  {workOrders.length}
                </p>
                <p className="text-sm text-slate-500">Total Orders</p>
              </div>
              <Command size={24} className="text-slate-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-display font-bold text-blue-600">
                  {workOrders.filter(w => w.status === 'in_progress').length}
                </p>
                <p className="text-sm text-slate-500">In Progress</p>
              </div>
              <ArrowsClockwise size={24} className="text-blue-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-display font-bold text-emerald-600">
                  {workOrders.filter(w => w.status === 'completed').length}
                </p>
                <p className="text-sm text-slate-500">Completed</p>
              </div>
              <CheckCircle size={24} className="text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-display font-bold text-slate-600">
                  {workOrders.filter(w => w.status === 'not_started').length}
                </p>
                <p className="text-sm text-slate-500">Not Started</p>
              </div>
              <Clock size={24} className="text-slate-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Work Orders Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-display">Work Orders</CardTitle>
          <CardDescription>
            Click on a work order to view details and submit updates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading work orders...</div>
          ) : workOrders.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No work orders found</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Crew Leader</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-24">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workOrders.map((wo) => (
                  <TableRow
                    key={wo.id}
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleSelectWO(wo)}
                    data-testid={`wo-row-${wo.id}`}
                  >
                    <TableCell className="font-medium">{wo.title}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-slate-400" />
                        {wo.crew_leader_name || 'Unassigned'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {wo.location && (
                        <div className="flex items-center gap-1">
                          <MapPin size={14} className="text-slate-400" />
                          {wo.location}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {wo.due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar size={14} className="text-slate-400" />
                          {new Date(wo.due_date).toLocaleDateString()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(wo.status)}</TableCell>
                    <TableCell>
                      <Button variant="outline" size="sm">View</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Work Order Detail Dialog */}
      <Dialog open={!!selectedWO} onOpenChange={(open) => !open && setSelectedWO(null)}>
        <DialogContent className="max-w-2xl">
          {selectedWO && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <DialogTitle className="font-display text-xl">{selectedWO.title}</DialogTitle>
                    <DialogDescription className="mt-1">
                      {selectedWO.description || 'No description provided'}
                    </DialogDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <AskAgentButton
                      prompts={woAgentPrompts}
                      onPromptSelect={(prompt, ctx) => openCopilot(prompt, ctx)}
                    />
                    {getStatusBadge(selectedWO.status)}
                  </div>
                </div>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="flex items-center gap-2 text-sm">
                  <User size={16} className="text-slate-400" />
                  <span className="text-slate-600">Assigned to:</span>
                  <span className="font-medium">{selectedWO.crew_leader_name || 'Unassigned'}</span>
                </div>
                {selectedWO.location && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin size={16} className="text-slate-400" />
                    <span className="text-slate-600">Location:</span>
                    <span className="font-medium">{selectedWO.location}</span>
                  </div>
                )}
                {selectedWO.due_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar size={16} className="text-slate-400" />
                    <span className="text-slate-600">Due:</span>
                    <span className="font-medium">{new Date(selectedWO.due_date).toLocaleDateString()}</span>
                  </div>
                )}
                {selectedWO.required_classification && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600">Classification:</span>
                    <Badge variant="outline">{selectedWO.required_classification}</Badge>
                  </div>
                )}
              </div>

              {/* Quick Canvass Integration */}
              {isSupervisorPlus() && !isAuditor() && (
                <div className="border rounded-lg p-4 bg-slate-50 mb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <LinkIcon size={18} className="text-primary" />
                        Quick Canvass Integration
                      </h4>
                      {selectedWO.linked_canvass_job ? (
                        <div className="mt-1">
                          <div className="text-sm text-slate-600 flex items-center gap-2">
                            <span>Linked to job: <span className="font-medium">{selectedWO.linked_canvass_job.title}</span></span>
                            <Badge variant="outline" className="text-xs">
                              {selectedWO.linked_canvass_job.bidding_status || selectedWO.linked_canvass_job.status}
                            </Badge>
                          </div>
                          {selectedWO.assigned_crew_leader_id && (
                            <p className="text-sm text-emerald-600 mt-1 flex items-center gap-1">
                              <CheckCircle size={14} />
                              Assigned to: <span className="font-medium">{selectedWO.assigned_crew_leader_name || 'Assigned'}</span>
                              {selectedWO.assigned_at && ` on ${new Date(selectedWO.assigned_at).toLocaleDateString()}`}
                            </p>
                          )}
                        </div>
                      ) : selectedWO.needs_canvass === false ? (
                        <p className="text-sm text-slate-500 mt-1">
                          Excluded from Quick Canvass (manual assignment)
                        </p>
                      ) : (
                        <p className="text-sm text-slate-500 mt-1">
                          Will auto-sync to Quick Canvass
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {selectedWO.linked_canvass_job ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigateToCanvassJob(selectedWO.linked_canvass_job.id)}
                          data-testid="view-canvass-job-btn"
                        >
                          <ArrowSquareOut size={16} className="mr-1" />
                          View in Quick Canvass
                        </Button>
                      ) : !selectedWO.needs_canvass ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleSendToCanvass}
                          disabled={sendToCanvassLoading || selectedWO.status === 'completed'}
                          data-testid="send-to-canvass-btn"
                        >
                          {sendToCanvassLoading ? (
                            <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                          ) : (
                            <PaperPlaneTilt size={16} className="mr-1" />
                          )}
                          Send to Quick Canvass
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}

              {/* Request Crew Card - PCC to Quick Canvass Bridge */}
              {isSupervisorPlus() && !isAuditor() && selectedWO.status !== 'completed' && (
                <div className="border rounded-lg p-4 bg-gradient-to-r from-indigo-50 to-purple-50 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Users size={18} className="text-indigo-600" />
                        Request Crew via Quick Canvass
                      </h4>
                      <p className="text-sm text-slate-600 mt-1">
                        Define crew needs and queue for supervisor assignment
                      </p>
                    </div>
                    {selectedWO.crew_request_status && (
                      <Badge className={
                        selectedWO.crew_request_status === 'queued' ? 'bg-blue-100 text-blue-800' :
                        selectedWO.crew_request_status === 'in_progress' ? 'bg-emerald-100 text-emerald-800' :
                        selectedWO.crew_request_status === 'completed' ? 'bg-green-100 text-green-800' :
                        'bg-slate-100 text-slate-600'
                      }>
                        {selectedWO.crew_request_status?.replace('_', ' ').toUpperCase()}
                      </Badge>
                    )}
                  </div>

                  {/* Crew Requirements */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white rounded-md p-3 border">
                      <label className="text-xs font-medium text-slate-500 block mb-1">Crew Leaders</label>
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        value={crewRequirements.crew_leader}
                        onChange={(e) => setCrewRequirements({...crewRequirements, crew_leader: parseInt(e.target.value) || 0})}
                        className="h-8"
                        data-testid="crew-leader-input"
                      />
                    </div>
                    <div className="bg-white rounded-md p-3 border">
                      <label className="text-xs font-medium text-slate-500 block mb-1">Mechanics</label>
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        value={crewRequirements.mechanic}
                        onChange={(e) => setCrewRequirements({...crewRequirements, mechanic: parseInt(e.target.value) || 0})}
                        className="h-8"
                        data-testid="mechanic-input"
                      />
                    </div>
                    <div className="bg-white rounded-md p-3 border">
                      <label className="text-xs font-medium text-slate-500 block mb-1">Crew Members</label>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={crewRequirements.crew_member}
                        onChange={(e) => setCrewRequirements({...crewRequirements, crew_member: parseInt(e.target.value) || 0})}
                        className="h-8"
                        data-testid="crew-member-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-sm text-slate-600">
                      Total crew needed: <span className="font-bold text-indigo-700">
                        {Object.values(crewRequirements).reduce((sum, val) => sum + val, 0)}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveCrewRequirements}
                        data-testid="save-crew-requirements-btn"
                      >
                        Save Draft
                      </Button>
                      <Dialog open={crewRequestDialogOpen} onOpenChange={setCrewRequestDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700"
                            disabled={selectedWO.crew_request_status === 'queued' || selectedWO.crew_request_status === 'in_progress'}
                            data-testid="queue-crew-request-btn"
                          >
                            <Queue size={16} className="mr-1" />
                            Queue Request
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Queue Crew Request</DialogTitle>
                            <DialogDescription>
                              This will create a staffing request in Quick Canvass for supervisors to action.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium text-sm">{selectedWO.title}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                Crew needed: {Object.values(crewRequirements).reduce((sum, val) => sum + val, 0)} total
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Label>Canvass Mode</Label>
                              <Select value={crewRequestMode} onValueChange={setCrewRequestMode}>
                                <SelectTrigger data-testid="crew-request-mode">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="DRAFT">Draft Mode (Turn-based picks)</SelectItem>
                                  <SelectItem value="OPEN_BID">Open Bid (All bid at once)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Priority</Label>
                              <Select value={crewRequestPriority} onValueChange={setCrewRequestPriority}>
                                <SelectTrigger data-testid="crew-request-priority">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="normal">Normal</SelectItem>
                                  <SelectItem value="urgent">Urgent</SelectItem>
                                  <SelectItem value="emergency">Emergency</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label>Notes (optional)</Label>
                              <Textarea
                                value={crewRequestNotes}
                                onChange={(e) => setCrewRequestNotes(e.target.value)}
                                placeholder="Any special instructions for the canvass..."
                                data-testid="crew-request-notes"
                              />
                            </div>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setCrewRequestDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button
                              onClick={handleQueueCrewRequest}
                              disabled={crewRequestLoading}
                              className="bg-indigo-600 hover:bg-indigo-700"
                              data-testid="confirm-queue-request-btn"
                            >
                              {crewRequestLoading ? (
                                <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                              ) : (
                                <Queue size={16} className="mr-1" />
                              )}
                              Queue Request
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              )}

              {/* Updates */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Updates ({updates.length})</h3>
                  {canSubmitUpdate() && selectedWO.status !== 'completed' && (
                    <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-primary" data-testid="add-update-btn">
                          <Plus size={16} className="mr-1" />
                          Add Update
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Submit Update</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Progress: {newUpdate.progress_percent}%</Label>
                            <Input
                              type="range"
                              min="0"
                              max="100"
                              value={newUpdate.progress_percent}
                              onChange={(e) => setNewUpdate({ ...newUpdate, progress_percent: parseInt(e.target.value) })}
                              data-testid="progress-slider"
                            />
                            <Progress value={newUpdate.progress_percent} className="h-2" />
                          </div>
                          <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                              value={newUpdate.notes}
                              onChange={(e) => setNewUpdate({ ...newUpdate, notes: e.target.value })}
                              placeholder="What work was completed?"
                              data-testid="update-notes-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Risks</Label>
                            <Input
                              value={newUpdate.risks}
                              onChange={(e) => setNewUpdate({ ...newUpdate, risks: e.target.value })}
                              placeholder="Any risks identified?"
                              data-testid="update-risks-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Blockers</Label>
                            <Input
                              value={newUpdate.blockers}
                              onChange={(e) => setNewUpdate({ ...newUpdate, blockers: e.target.value })}
                              placeholder="Any blockers?"
                              data-testid="update-blockers-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Next Actions</Label>
                            <Input
                              value={newUpdate.next_actions}
                              onChange={(e) => setNewUpdate({ ...newUpdate, next_actions: e.target.value })}
                              placeholder="What's next?"
                              data-testid="update-next-input"
                            />
                          </div>
                        </div>
                        <DialogFooter>
                          <Button onClick={handleSubmitUpdate} className="bg-primary" data-testid="submit-update-btn">
                            Submit Update
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>

                {updates.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No updates yet</p>
                ) : (
                  <div className="space-y-4 max-h-64 overflow-y-auto">
                    {updates.map((update) => (
                      <div key={update.id} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{update.author_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(update.created_at).toLocaleString()}
                          </span>
                        </div>
                        <Progress value={update.progress_percent} className="h-2 mb-2" />
                        <p className="text-sm text-slate-600">{update.notes}</p>
                        {update.risks && (
                          <div className="flex items-center gap-1 text-sm text-amber-600 mt-2">
                            <Warning size={14} />
                            <span>Risk: {update.risks}</span>
                          </div>
                        )}
                        {update.blockers && (
                          <div className="text-sm text-red-600 mt-1">
                            Blocker: {update.blockers}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommandCenterPage;
